import { CoreModule, View } from "@novorender/api";
import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from "gl-matrix";

import { getSnapInfo, PointSnapInfo } from "features/render/measureSnap";

import type { PointLine, PointLineDraggingPoint, PointLinePoint } from "./pointLineSlice";

/**
 * @returns pointLine points.
 *          If it contains the point being dragged - instead return the dragging position.
 *          If it's also snapped - return snap position instead.
 */
export function getPointLineWithDrag(
    pointLine: PointLine,
    isClosed: boolean,
    draggingPointIndex: number,
    dragPos: ReadonlyVec3,
    snapTo: number | null,
) {
    return pointLine.points.map((p, pointIdx) => {
        // When dragging first point in a closed line - sync it with the last point
        if (
            pointIdx === draggingPointIndex ||
            (isClosed && draggingPointIndex === 0 && pointIdx === pointLine.points.length - 1)
        ) {
            const position = snapTo !== null ? pointLine.points[snapTo].position : dragPos;
            return { ...p, position, snap: { type: "vertex", connection: position } as PointSnapInfo };
        }
        return p;
    });
}

export function isPointLineClosed(points: PointLinePoint[]) {
    return points.length > 2 && vec3.exactEquals(points[0].position, points[points.length - 1].position);
}

export function getPointToSnapTo({
    pointLine,
    pointLineIndex,
    draggingPoint,
    currentIndex,
}: {
    pointLine: PointLine;
    pointLineIndex: number;
    draggingPoint: PointLineDraggingPoint | null;
    currentIndex: number;
}) {
    const isClosed = isPointLineClosed(pointLine.points);
    if (isClosed) {
        return null;
    }

    const { points } = pointLine;
    const isCurrentPointLine = pointLineIndex === currentIndex;

    // Can snap to the first point when placing a new point
    if (!draggingPoint && points.length > 2 && isCurrentPointLine) {
        return 0;
    }

    // Can snap to the last point when dragging the first point
    if (points.length > 3 && draggingPoint?.pointLineIdx === pointLineIndex && draggingPoint.pointIdx === 0) {
        return points.length - 1;
    }

    // Can snap to the first point when dragging the last point
    if (
        points.length > 3 &&
        draggingPoint?.pointLineIdx === pointLineIndex &&
        draggingPoint.pointIdx === points.length - 1
    ) {
        return 0;
    }

    return null;
}

export function getCenter(points: PointLinePoint[]) {
    const center = vec3.create();
    const isClosed = isPointLineClosed(points);
    const count = isClosed ? points.length - 1 : points.length;
    for (let i = 0; i < count; i++) {
        vec3.add(center, center, points[i].position);
    }
    return vec3.scale(center, center, 1 / count);
}

export async function getPointLineSnapInfo({
    view,
    position2d,
    hoverEnt,
    draggingPoint,
    currentPointLineIdx,
    pointLines,
}: {
    view: View;
    position2d: ReadonlyVec2;
    hoverEnt: Awaited<ReturnType<CoreModule["pickMeasureEntityOnCurrentObject"]>> | undefined;
    draggingPoint: PointLineDraggingPoint | null;
    currentPointLineIdx: number;
    pointLines: PointLine[];
}): Promise<{ snapInfo: PointSnapInfo | undefined; snapTo: number | null }> {
    const currentPointLine = draggingPoint ? pointLines[draggingPoint.pointLineIdx] : pointLines[currentPointLineIdx];
    const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });

    let snapInfo: PointSnapInfo | undefined = undefined;

    let shouldSnapToPoint = false;
    const snapTo = getPointToSnapTo({
        pointLine: currentPointLine,
        pointLineIndex: draggingPoint ? draggingPoint.pointLineIdx : currentPointLineIdx,
        draggingPoint,
        currentIndex: currentPointLineIdx,
    });
    if (snapTo !== null) {
        const snapToPoint = currentPointLine.points[snapTo];
        const p2d = toScreenSpace(snapToPoint.position);
        if (p2d) {
            const dist = vec2.dist(p2d, position2d);
            if (dist < 10) {
                snapInfo = { type: "vertex", connection: snapToPoint.position };
                shouldSnapToPoint = true;
            }
        }
    }

    if (!shouldSnapToPoint) {
        snapInfo =
            hoverEnt?.entity &&
            hoverEnt.connectionPoint &&
            (await getSnapInfo({
                view,
                entity: hoverEnt.entity,
                connectionPoint: hoverEnt.connectionPoint,
                corners: true,
            }));
    }

    return { snapInfo, snapTo: shouldSnapToPoint ? snapTo : null };
}
